import { useAtom, useAtomValue, useSetAtom } from "jotai";
import {
  useEffect,
  useMemo,
  useState,
  useRef,
  useCallback,
} from "react";
import { useTranslation } from "react-i18next";
import {
  proposalsPageSearchState,
  proposalsPageProductFilterState,
  proposalsPageShowModeState,
  proposalsPageTotalState,
  proposalsPageSelectedItemsState,
} from "store/ProposalsPageStore";
import { ColumnOptions } from "./components/ColumnOptions";
import { ColumnOptionsButton } from "library/greco-components";
import { Navigation } from "components/Navigation/Navigation";
import { ListPageTable, ListPageTableHandle } from "./ListPageTable";
import { useAppTheme } from "hooks/useAppTheme";
import { useLoadOffers } from "store/ProposalsPageHooks";
import { useMainContext } from "providers/MainProvider";
import { ColumnOptionsUtils } from "library/greco-components";
import { toVisibleFilterColumns } from "library/utils/columnOptionsState";
import { InvalidateOfferDialog } from "./components/InvalidateOfferDialog";
import { MarkOfferAsErrorDialog } from "./components/MarkOfferAsErrorDialog";
import { useHasAnyRole } from "hooks/useUserRoles";
import { RolePermissions } from "library/constants/roles";
import { OffersShowMode } from "state/queries/useGetOffers";
import {
  SelectionToolbar,
  SELECTION_TOOLBAR_HEIGHT,
} from "./components/SelectionToolbar";
import { BulkPrefillDialog } from "components/BulkPrefillDialog";
import { ExportButtons } from "./components/ExportButtons";
import { Button, Tooltip } from "@fluentui/react-components";
import { DocumentMultipleRegular } from "@fluentui/react-icons";

const ProposalList = () => {
  const { columns, columnsReady } = useMainContext();
  const [searchTerm, setSearchTerm] = useAtom(proposalsPageSearchState);
  const [productFilter, setProductFilter] = useAtom(
    proposalsPageProductFilterState
  );
  const [showMode, setShowMode] = useAtom(proposalsPageShowModeState);
  const canChooseShowMode = useHasAnyRole(
    RolePermissions.canChooseOffersShowMode()
  );
  const totalCount = useAtomValue(proposalsPageTotalState);
  const setSelectedItems = useSetAtom(proposalsPageSelectedItemsState);
  const selectedItems = useAtomValue(proposalsPageSelectedItemsState);
  const tableRef = useRef<ListPageTableHandle>(null);
  const theme = useAppTheme();
  const loadOffers = useLoadOffers();

  const [isColumnOptionsOpen, setIsColumnOptionsOpen] = useState(false);
  // The panel unmounts on close, so focus is handed back to its opener here.
  const columnOptionsOpenerRef = useRef<HTMLElement | null>(null);
  const [isBulkPrefillOpen, setIsBulkPrefillOpen] = useState(false);
  const [localSearchValue, setLocalSearchValue] = useState(searchTerm);
  const areAnyColumnsFiltered = useMemo(
    () =>
      toVisibleFilterColumns(columns).some((col) =>
        ColumnOptionsUtils.isColumnFiltered(col.filter)
      ),
    [columns]
  );
  const { t } = useTranslation();

  // Track initial values to prevent duplicate initial loads
  const initialSearchTerm = useRef(searchTerm);
  const initialProductFilter = useRef(productFilter);
  const initialShowMode = useRef(showMode);
  const hasShowModeChanged = useRef(false);
  const hasInitialLoad = useRef(false);
  const hasProductFilterChanged = useRef(false);
  const hasSearchChanged = useRef(false);

  // Initial load is handled by MainProvider (Effect 1) with the correct columns.
  // Mark hasInitialLoad once columnsReady so subsequent effects (search, filter) work.
  useEffect(() => {
    if (columnsReady) {
      hasInitialLoad.current = true;
    }
  }, [columnsReady]);

  // Sync local search value with atom state on mount
  useEffect(() => {
    setLocalSearchValue(searchTerm);
  }, [searchTerm]);

  // Throttled search handler with cleanup
  useEffect(() => {
    const timer = setTimeout(() => {
      if (localSearchValue !== searchTerm) {
        hasSearchChanged.current = true; // Mark that search has been changed
        setSearchTerm(localSearchValue);
      }
    }, 500);

    return () => clearTimeout(timer);
  }, [localSearchValue, searchTerm, setSearchTerm]);

  // Trigger search when searchTerm changes
  useEffect(() => {
    // Skip if initial load hasn't happened yet
    if (!hasInitialLoad.current) return;

    // Skip if this is the initial value AND search hasn't been changed by user
    if (searchTerm === initialSearchTerm.current && !hasSearchChanged.current)
      return;

    loadOffers({
      skip: 0,
      top: 40,
      reset: true,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm]);

  // Trigger reload when product filter changes
  useEffect(() => {
    // Skip if this is the initial value AND it hasn't changed before
    if (
      productFilter === initialProductFilter.current &&
      !hasProductFilterChanged.current
    ) {
      return;
    }

    // Mark that the filter has been changed at least once
    hasProductFilterChanged.current = true;

    loadOffers({
      skip: 0,
      top: 40,
      reset: true,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productFilter]);

  // Trigger reload when the (admin) show mode changes
  useEffect(() => {
    if (showMode === initialShowMode.current && !hasShowModeChanged.current) {
      return;
    }
    hasShowModeChanged.current = true;
    tableRef.current?.clearSelection();

    loadOffers({
      skip: 0,
      top: 40,
      reset: true,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showMode]);

  const handleReload = () => {
    loadOffers({
      skip: 0,
      top: 40,
      reset: true,
    });
  };

  const handleLoadMore = (skip: number, top: number) => {
    loadOffers({
      skip,
      top,
      reset: false,
    });
  };

  const handleSortChange = () => {
    // Clear selection before reloading sorted data
    tableRef.current?.clearSelection();
    // When sort changes, reload from beginning
    loadOffers({
      skip: 0,
      top: 40,
      reset: true,
    });
  };

  const handleSearch = (searchValue: string) => {
    setLocalSearchValue(searchValue);
  };

  const handleProductFilterChange = (value: "all" | "mtpl" | "casco") => {
    setProductFilter(value);
  };

  const handleShowModeChange = (value: OffersShowMode) => {
    setShowMode(value);
  };

  const handleSelectionChanged = useCallback(
    (items: any[]) => {
      setSelectedItems(items);
    },
    [setSelectedItems]
  );

  const handleClearSelection = useCallback(() => {
    tableRef.current?.clearSelection();
  }, []);

  const columnOptions = (
    <div
      style={{
        display: "contents",
      }}
    >
      <ColumnOptionsButton
        data-help="offers-column-options"
        t={t as any}
        isActive={areAnyColumnsFiltered}
        onClick={() => {
          columnOptionsOpenerRef.current = document.activeElement as HTMLElement;
          setIsColumnOptionsOpen(true);
        }}
        style={{
          color: theme.isDark
            ? theme.semanticColors.bodyText
            : theme.semanticColors.bodyText,
          backgroundColor: theme.isDark
            ? theme.semanticColors.bodyBackground
            : theme.semanticColors.bodyBackground,
        }}
      />
      <ColumnOptions
        isOpen={isColumnOptionsOpen}
        closePanel={() => {
          setIsColumnOptionsOpen(false);
          requestAnimationFrame(() => columnOptionsOpenerRef.current?.focus());
        }}
      />
    </div>
  );

  const rightSideButtons = (
    <>
      <ExportButtons />
      <Tooltip
        content={t("qtg.bulkPrefill.buttonTooltip", "Bulk Vehicle Prefill")}
        relationship="label"
      >
        <Button
          data-help="offers-bulk-prefill"
          appearance="secondary"
          icon={<DocumentMultipleRegular />}
          onClick={() => setIsBulkPrefillOpen(true)}
        >
          {t("qtg.bulkPrefill.button", "Bulk Prefill")}
        </Button>
      </Tooltip>
    </>
  );

  return (
    <div
      style={{
        width: "100%",
        height: "calc(100vh - 50px)", // Full viewport height
        display: "flex",
        flexDirection: "column",
        background: theme.isDark
          ? theme.semanticColors.bodyBackground
          : theme.semanticColors.bodyBackground,
      }}
    >
      <Navigation
        columnOptions={columnOptions}
        displaySearch
        displayReload
        onReload={handleReload}
        onSearch={handleSearch}
        searchValue={localSearchValue}
        showNewMenu
        rightSideButtons={rightSideButtons}
        productFilter={productFilter}
        onProductFilterChange={handleProductFilterChange}
        showMode={showMode}
        onShowModeChange={canChooseShowMode ? handleShowModeChange : undefined}
        totalCount={totalCount}
      />
      <div
        style={{
          flex: 1,
          width: "100%",
          height: selectedItems.length > 0
            ? `calc(100vh - 100px - ${SELECTION_TOOLBAR_HEIGHT}px)`
            : "calc(100vh - 100px)",
          position: "relative",
          overflow: "hidden",
          background: theme.isDark
            ? theme.semanticColors.bodyBackground
            : theme.semanticColors.bodyBackground,
        }}
      >
        <ListPageTable
          ref={tableRef}
          columns={columns}
          onLoadMore={handleLoadMore}
          onSortChange={handleSortChange}
          onSelectionChanged={handleSelectionChanged}
        />
      </div>
      <SelectionToolbar onClearSelection={handleClearSelection} />
      <InvalidateOfferDialog />
      <MarkOfferAsErrorDialog />
      <BulkPrefillDialog
        isOpen={isBulkPrefillOpen}
        onClose={() => setIsBulkPrefillOpen(false)}
        onSuccess={handleReload}
      />
    </div>
  );
};
export default ProposalList;
